import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { SavedListings } from '../../../SavedListings'
import { useAnalysisMode } from '../../contexts/AnalysisModeContext'
import { useAnalysisMapSectionData } from '../../contexts/MapSectionDataContext'

export interface AnalysisListingsTabProps {
  type: 'purchase' | 'rental'
}

/**
 * Shared ListingsTab for read-only display of saved listings.
 * Used by public page types (land, estimate).
 * Renders SavedListings from pre-fetched entity data.
 */
export const ListingsTab: React.FC<AnalysisListingsTabProps> = ({ type }) => {
  const { entity } = useAnalysisMode()
  const { setActiveSection, updateSectionData } = useAnalysisMapSectionData()
  const { t, i18n } = useTranslation('listings')

  const language = (i18n.language?.slice(0, 2) || 'en') as
    | 'en'
    | 'fr'
    | 'de'
    | 'it'

  const translations = {
    loading: t('saved.loading'),
    emptyTitle: t('saved.empty'),
    emptyDescription: t('saved.emptyDescription'),
    totalSaved: t('saved.totalSaved'),
    listings: t('saved.listings'),
    averagePrice: t('saved.averagePrice'),
    perListing: t('saved.perListing'),
    avgPricePerSqm: t('saved.avgPricePerSqm'),
    livingArea: t('saved.livingArea'),
    avgLivingArea: t('saved.avgLivingArea'),
    priceRange: t('saved.priceRangeLabel'),
    to: t('common.to'),
    avgRooms: t('saved.avgRooms'),
    perListing2: t('saved.perListing2'),
    sqm: t('common.sqm'),
    rooms: t('common.rooms'),
    built: t('common.built'),
    listed: t('common.listed'),
    active: t('common.active'),
    expired: t('common.expired'),
    removeFromSaved: t('saved.removeFromSaved'),
    perSqm: t('common.perSqm'),
    propertyImage: t('common.propertyImage'),
    propertyListing: t('common.propertyListing'),
    addressNotAvailable: t('common.addressNotAvailable'),
    priceOnRequest: t('common.priceOnRequest'),
    today: t('common.today'),
    yesterday: t('common.yesterday'),
    daysAgo: t('common.daysAgo'),
    weeksAgo: t('common.weeksAgo'),
    monthsAgo: t('common.monthsAgo'),
    yearsAgo: t('common.yearsAgo'),
  }

  // Extract saved listings from entity based on type
  const savedListings = useMemo(() => {
    let listingsData: any[]

    if (type === 'purchase') {
      listingsData =
        entity?.purchase_listings ||
        entity?.purchaseListings ||
        entity?.savedListings ||
        []
    } else {
      listingsData = entity?.rental_listings || entity?.rentalListings || []
    }

    return Array.isArray(listingsData) ? listingsData : []
  }, [entity, type])

  // Push listings data to map context
  useEffect(() => {
    const sectionType =
      type === 'purchase' ? 'purchase_listings' : 'rental_listings'
    setActiveSection(sectionType)
    if (savedListings.length > 0) {
      updateSectionData({ listings: savedListings })
    }
  }, [savedListings, type, setActiveSection, updateSectionData])

  // Handle listing click (no-op in read-only mode)
  const handleListingClick = () => {
    // In read-only mode, we don't have listing detail panels
  }

  return (
    <SavedListings
      listings={savedListings}
      onListingClick={handleListingClick}
      isLoading={false}
      translations={translations}
      language={language}
    />
  )
}
